import L from 'leaflet';
import _ from 'lodash';
import React from 'react';
import {GeoJSON} from 'react-leaflet';
import {connect} from 'react-redux';
import {setActiveIncidentFeature} from '../actions';
import {INCIDENT_PRESCRIPTION_STATUS, INCIDENT_TYPE_KEYS} from '../Constants';
import {
  bindBurntAreaPathStyle,
  BURNT_AREA_FILL_COLOR,
  BURNT_AREA_FILL_OPACITY,
  BURNT_AREA_STROKE_WIDTH
} from '../utils/leaflet_burnt_area_pattern';

// Stacking for DEECA/CFA bushfires: red/orange (going, contained) above blue (controlled).
const BUSHFIRE_MARKER_Z_INDEX_OFFSET = {
  controlled: 0,
  contained: 100,
  going: 200
};

class IncidentsLayer extends React.Component {
  onMapClick = (e, feature, layer) => {
    e.originalEvent._stopped = true;
    layer.closePopup();
    const { dispatch } = this.props;
    dispatch(setActiveIncidentFeature(feature));
  };

  render() {
    const { incidents } = this.props;
    const active_incident_types = incidents.layers.filter(layer => layer.active);
    const nodes = [];
    active_incident_types.forEach(meta_data => {
      if (meta_data.status && meta_data.id.startsWith('BURNS_')) {
        const key = meta_data.id.toLowerCase();
        const burns = incidents.data[key];
        if (burns) {
          this.pushBurnNodes(nodes, burns, incidents.polygons_map, meta_data);
        }
      } else {
        switch (meta_data.id) {
          case INCIDENT_TYPE_KEYS.BUSHFIRES:
            nodes.push(this.renderIncidentsLayer(incidents.data.bushfires));
            break;
          case INCIDENT_TYPE_KEYS.BURNS:
            nodes.push(this.renderBurnsLayer(incidents.data.burns, 'burns'));
            break;
          case INCIDENT_TYPE_KEYS.INCIDENT_POLYGONS:
            nodes.push(this.renderPolygonsLayer(incidents.polygons));
            break;
          case INCIDENT_TYPE_KEYS.CURRENT_BURNT_AREA:
            nodes.push(this.renderCurrentBurntAreaLayer(incidents.data.current_burnt_area));
            break;
          case INCIDENT_TYPE_KEYS.INCIDENT_PRESCRIPTIONS:
            nodes.push(...this.renderPrescriptionRingsLayer(active_incident_types, incidents));
            break;
          default:
        }
      }
    });
    return nodes.length === 0 ? null : <React.Fragment>{nodes}</React.Fragment>;
  }

  renderIncidentsLayer(bushfires) {
    if (bushfires) {
      return <GeoJSON key={INCIDENT_TYPE_KEYS.BUSHFIRES + bushfires.updated_at}
                      data={bushfires}
                      pointToLayer={this.bushfireMarker}
                      zIndex={5}
                      onEachFeature={this.propertyPopup}/>;
    }
  }

  pushBurnNodes(nodes, incidents, polygons_map, meta_data) {
    nodes.push(this.renderBurnsLayer(incidents, meta_data.id));
    nodes.push(this.renderPolygonsUnderlay(incidents, polygons_map, meta_data.id));
  }

  renderPolygonsUnderlay(incidents, polygons_map, key) {
    if (incidents && polygons_map) {
      // Tie the incident and polygon features together
      incidents.features.forEach(i => {
        const poly = polygons_map[i.eventId];
        if (poly) {
          poly.incident = i;
          i.polygon = poly;
          i.polygon.polyColor = i.polyColor;
          i.properties.Area = poly.properties.Area;
        }
      });
      // Create the GeoJSON for the polygon layer
      const polygons = {
        type: 'FeatureCollection',
        features: _.map(incidents.features, i => i.polygon).filter(f => f)
      };
      const polystyle = (f) => {
        return {
          fillColor: f.polyColor,
          weight: 1,
          opacity: 1.0,
          color: f.polyColor,
          fillOpacity: 0.5
        };
      };
      return <GeoJSON key={key + '_polys_' + incidents.updated_at}
                      data={polygons}
                      style={polystyle}
                      zIndex={4}
                      onEachFeature={this.polygonHover}/>;
    }
  }

  renderPolygonsLayer(polygons) {
    if (polygons) {
      return <GeoJSON key={INCIDENT_TYPE_KEYS.POLYGONS + polygons.updated_at}
                      data={polygons}
                      onEachFeature={this.propertyPopup}
                      zIndex={5}/>;
    }
  }

  burntAreaOnEachFeature = (feature, layer) => {
    bindBurntAreaPathStyle(layer);
    this.propertyPopup(feature, layer);
  };

  renderCurrentBurntAreaLayer(burntArea) {
    if (burntArea) {
      const style = () => ({
        fillColor: BURNT_AREA_FILL_COLOR,
        weight: BURNT_AREA_STROKE_WIDTH,
        opacity: 1.0,
        color: '#000000',
        fillOpacity: BURNT_AREA_FILL_OPACITY
      });
      return <GeoJSON key={INCIDENT_TYPE_KEYS.CURRENT_BURNT_AREA + burntArea.updated_at}
                      data={burntArea}
                      style={style}
                      zIndex={4}
                      onEachFeature={this.burntAreaOnEachFeature}/>;
    }
  }

  renderBurnsLayer(burns, key) {
    if (burns) {
      return <GeoJSON key={key + burns.updated_at}
                      data={burns}
                      pointToLayer={this.burnsMarker}
                      zIndex={5}
                      onEachFeature={this.propertyPopup}/>;
    }
  }

  renderPrescriptionRingsLayer(active_incident_types, incidents) {
    const nodes = [];
    active_incident_types.forEach(meta_data => {
      if (meta_data.id.startsWith('BURNS_')) {
        const key = meta_data.id.toLowerCase();
        const burns = incidents.data[key];
        if (burns) {
          nodes.push(<GeoJSON key={'fdi_prescription_' + key + incidents.prescriptions_updated_at}
                              data={burns}
                              pointToLayer={this.fdiPrescriptionMarker}
                              zIndex={5}/>);
          nodes.push(<GeoJSON key={'wind_prescription_' + key + incidents.prescriptions_updated_at}
                              data={burns}
                              pointToLayer={this.windPrescriptionMarker}
                              zIndex={5}/>);
        }
      }
    });
    return nodes;
  }

  bushfireMarker = (feature, latlng) => {
    const iconName = feature.icon;
    const zIndexOffset = _.get(BUSHFIRE_MARKER_Z_INDEX_OFFSET, iconName, 50);
    return L.marker(latlng,
      {
        icon: L.icon({
          iconUrl: require('../assets/incident_layer/' + iconName + '.png'),
          iconAnchor: [15, 15]
        }),
        zIndexOffset
      });
  };

  burnsMarker = (feature, latlng) => {
    return L.marker(latlng,
      { icon: L.icon({ iconUrl: require('../assets/incident_layer/' + feature.icon + '.png'), iconAnchor: [10, 9] }) });
  };

  _haloColor(prescriptionStatus, result) {
    if (prescriptionStatus === INCIDENT_PRESCRIPTION_STATUS.UNKNOWN_PRESCRIPTION) {
      return 'black';
    } else if (prescriptionStatus === INCIDENT_PRESCRIPTION_STATUS.NO_PRESCRIPTION
               || prescriptionStatus === INCIDENT_PRESCRIPTION_STATUS.PRESCRIPTION_NOT_LOADED) {
      return '#999999';
    } else if (!result) {
      return 'black';
    } else if (result.pass || result.under) {
      return '#00dd00';
    } else if (result.close) {
      return '#ffff00';
    } else {
      return '#ff0000';
    }
  }

  fdiPrescriptionMarker = (feature, latlng) => {
    const color = this._haloColor(feature.prescriptionStatus, feature.d1fdiResult);
    const weight = 2;
    const radius = 6;
    const opacity = 0.9;

    return L.circleMarker(latlng, { color, fill: false, weight, radius, opacity });
  };

  windPrescriptionMarker = (feature, latlng) => {
    const fdiColor = this._haloColor(feature.prescriptionStatus, feature.d1fdiResult);
    const color = this._haloColor(feature.prescriptionStatus, feature.wndResult);
    const weight = 2;
    const radius = 8;

    // If they are both green, they don't have to be so big.
    const opacity = (color === '#00dd00' && fdiColor === '#00dd00') ? 0.0 : 0.9;

    return L.circleMarker(latlng, { color, fill: false, weight, radius, opacity });
  };

  propertyPopup = (feature, layer) => {
    const { incidents } = this.props;
    const isActive = incidents.active && incidents.active.eventId === feature.eventId;
    if (!isActive) {
      layer.bindPopup(this.buildPopupFromProperties(feature.properties), { autoPan: false });
      layer.on('mouseover', () => {layer.openPopup();});
      layer.on('mouseout', () => {layer.closePopup();});
      layer.on('click', (e) => {this.onMapClick(e, feature, layer);});
    }
    feature.layer = layer;
  };

  polygonHover = (feature, layer) => {
    const overStyle = { weight: 3, fillOpacity: 0.8 };
    const outStyle = { weight: 1, fillOpacity: 0.5 };
    layer.on('click', (e) => {
      if (feature.incident) {
        this.onMapClick(e, feature.incident, feature.incident.layer);
      }
    });
    layer.on('mouseover', () => {
      layer.setStyle(overStyle);
      if (feature.incident.layer) {
        feature.incident.layer.openPopup();
      }
    });
    layer.on('mouseout', () => {
      layer.setStyle(outStyle);
      if (feature.incident.layer) {
        feature.incident.layer.closePopup();
      }
    });

    // Also bind events to the incident marker.
    if (feature.incident.layer) {
      feature.incident.layer.on('mouseover', () => {layer.setStyle(overStyle);});
      feature.incident.layer.on('mouseout', () => {layer.setStyle(outStyle);});
    }
  };

  buildPopupFromProperties = (properties) => {
    let popup = '<table>';
    Object.entries(properties).forEach(
      property => popup +=
        '<tr><td class=\'property-key\'>' +
        property[0].replace(/_/g, ' ') +
        '</td><td>' +
        (property[1] ? property[1] : '')
        +
        '</td></tr>');
    return popup + '</table>';
  };
}

const mapStateToProps = state => {
  return {
    incidents: state.incidents
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch: dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(IncidentsLayer);



// WEBPACK FOOTER //
// ./src/containers/IncidentsLayer.js